/**
 * The operator's default tag categories are saved as a change, never as the whole list
 * (backend plan 2026-10-29-sandbox-default-tag-categories-sync, WP-C).
 *
 * `PUT /v2/edit/tour_operator/{id}/` takes `default_tag_categories_changes`:
 *
 *   add:    [{category_id, fields: {<field>: value}}]   a new row; every field is sent
 *   update: [{id, fields: {<field>: {from, to}}}]        only the fields that changed
 *   remove: [{id}]
 *
 * The backend applies an update only when every `from` still equals the stored value (else
 * 409 `defaults_conflict`), and never writes a row the change does not name. So the change is
 * computed against the snapshot the page LOADED, not against the form: a stale tab then can
 * no longer delete rows added elsewhere or revert flags changed elsewhere.
 *
 * Pure functions only; the pages send, and refusedSettingsSave.js handles the refusals.
 */
import {
  OPERATOR_DEFAULT_ONLY_BOOL_SETTINGS,
  TAG_CATEGORY_BOOL_SETTINGS,
  TAG_CATEGORY_INT_SETTINGS,
} from '../utils/tagCategories';

const BOOL_FIELDS = [...TAG_CATEGORY_BOOL_SETTINGS, ...OPERATOR_DEFAULT_ONLY_BOOL_SETTINGS];
const BOOL_FIELD_SET = new Set(BOOL_FIELDS);

/** What a change may set on a row (backend tag_category_defaults.DELTA_FIELDS). */
export const DEFAULT_ROW_FIELDS = ['sequence', ...TAG_CATEGORY_INT_SETTINGS, ...BOOL_FIELDS];

// As the backend stores it: a flag the row lacks is off, a count it lacks is 0.
const stored = (name, value) => (BOOL_FIELD_SET.has(name) ? !!value : Number(value || 0));

const key = (id) => (id == null ? null : String(id));

// A new row sends every field as the form shows it: the backend turns an absent flag off, and
// the form must not depend on that. Only an unset sequence is left out (the backend puts the
// row last).
function addFields(row) {
  const fields = {};
  DEFAULT_ROW_FIELDS.forEach((name) => {
    if (name === 'sequence' && !Number.isFinite(row.sequence)) return;
    fields[name] = stored(name, row[name]);
  });
  return fields;
}

/**
 * The change from `loadedRows` (the server's list, as last loaded or saved) to `currentRows`
 * (the form's list), or null when there is none.
 *
 * - A row is matched to the snapshot by its `id`; its changed fields are an update, `from` the
 *   loaded value. A reorder is sequence updates.
 * - A snapshot row missing from the form is a remove.
 * - A row the snapshot does not hold is an add, by its category; one with no category picked
 *   yet adds nothing.
 * - A row whose category changed is removed and added again (a row never changes category in
 *   place).
 * - Client-only keys (`idx`, `is_new`, ...) and the array order are ignored.
 */
export function computeDefaultTagCategoriesChanges(loadedRows, currentRows) {
  const loadedById = new Map();
  (loadedRows || []).forEach((row) => {
    if (row && row.id != null) loadedById.set(key(row.id), row);
  });

  const add = [];
  const update = [];
  const kept = new Set();
  (currentRows || []).forEach((row) => {
    if (!row) return;
    const id = key(row.id);
    const loaded = id != null && !kept.has(id) ? loadedById.get(id) : undefined;
    if (loaded && key(loaded.category?.id) === key(row.category?.id)) {
      kept.add(id);
      const fields = {};
      DEFAULT_ROW_FIELDS.forEach((name) => {
        const from = stored(name, loaded[name]);
        const to = stored(name, row[name]);
        if (from !== to) fields[name] = { from, to };
      });
      if (Object.keys(fields).length > 0) update.push({ id: loaded.id, fields });
      return;
    }
    if (row.category?.id == null) return;
    add.push({ category_id: row.category.id, fields: addFields(row) });
  });

  const remove = [];
  loadedById.forEach((row, id) => {
    if (!kept.has(id)) remove.push({ id: row.id });
  });

  if (add.length === 0 && update.length === 0 && remove.length === 0) return null;
  return { add, update, remove };
}

/**
 * The PUT body for a General-settings save: `default_tag_categories` is never sent; its change
 * against `loadedRows` goes as `default_tag_categories_changes`, only when there is one.
 */
export function withDefaultTagCategoriesChanges(body, loadedRows) {
  const { default_tag_categories: currentRows, ...rest } = body || {};
  if (currentRows === undefined) return rest;
  const changes = computeDefaultTagCategoriesChanges(loadedRows, currentRows);
  return changes ? { ...rest, default_tag_categories_changes: changes } : rest;
}

/** 409: the rows changed elsewhere since the page loaded them; nothing was saved. */
export const isDefaultsConflict = (res) => res?.statusCode === 409 && res?.data?.code === 'defaults_conflict';

/** 400: the change breaks a rule or is malformed; nothing was saved. */
export const isDefaultsInvalid = (res) => res?.statusCode === 400 && res?.data?.code === 'defaults_invalid';

export const DEFAULTS_CONFLICT_MESSAGE = 'Default tag categories were changed elsewhere — reloaded; re-apply your change';

/**
 * One line for a 400 `defaults_invalid` body, `errors: [{op?, index?, id | category_id?, field?,
 * message}]`: each error prefixed with its row's category name (looked up in `rows`, the form's
 * list) and its field; a list-wide error has neither.
 */
export function defaultsInvalidMessage(body, rows) {
  const errors = Array.isArray(body?.errors) ? body.errors : null;
  if (!errors || errors.length === 0) {
    return body?.detail || "Default tag categories can't be saved";
  }
  const nameOf = (error) => {
    const match = (rows || []).find((row) => (
      error.id != null
        ? key(row?.id) === key(error.id)
        : error.category_id != null && key(row?.category?.id) === key(error.category_id)
    ));
    return match?.category?.name || null;
  };
  const parts = errors.map((error) => [nameOf(error), error.field, error.message].filter(Boolean).join(': '));
  return `${body.detail || "Default tag categories can't be saved:"} ${parts.join('; ')}`;
}
